'use client';

import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import { AlertTriangle } from 'lucide-react';

// Backend integration: replace with API call to /api/reports/breakdown-frequency
const breakdownData = [
  { reg: 'GP 23-441', count: 11, lemon: true },
  { reg: 'WC 77-892', count: 9, lemon: true },
  { reg: 'GP 11-203', count: 6, lemon: false },
  { reg: 'NW 44-119', count: 5, lemon: false },
  { reg: 'GP 88-567', count: 4, lemon: false },
  { reg: 'EC 31-774', count: 3, lemon: false },
  { reg: 'GP 55-002', count: 3, lemon: false },
  { reg: 'KZN 19-441', count: 2, lemon: false },
  { reg: 'LP 60-338', count: 1, lemon: false },
];

interface TooltipPayloadItem {
  value: number;
  payload: { reg: string; count: number; lemon: boolean };
}

const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: TooltipPayloadItem[] }) => {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload;
  return (
    <div className="card-elevated px-3 py-2.5 shadow-elevated text-xs">
      <p className="font-mono-data font-semibold text-foreground mb-1">{d.reg}</p>
      <p className="text-muted-foreground">Breakdowns: <span className="text-foreground font-semibold">{d.count}</span></p>
      {d.lemon && (
        <p className="text-warning mt-1 flex items-center gap-1">
          <AlertTriangle size={10} />
          High-failure asset
        </p>
      )}
    </div>
  );
};

export default function BreakdownFrequencyChart() {
  return (
    <div className="card-surface p-5 h-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-foreground">Breakdown Frequency</h2>
          <p className="text-xs text-muted-foreground mt-0.5">All vehicles · Last 30 days · High-failure assets flagged</p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: 'var(--danger)' }} />
            <span className="text-muted-foreground">Lemon</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: 'var(--primary)' }} />
            <span className="text-muted-foreground">Normal</span>
          </span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={breakdownData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis
            dataKey="reg"
            tick={{ fill: 'var(--muted-foreground)', fontSize: 11, fontFamily: 'var(--font-ibm-plex-mono)' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <ReferenceLine y={7} stroke="var(--warning)" strokeDasharray="4 4" strokeWidth={1.5} label={{ value: 'Alert threshold', fill: 'var(--warning)', fontSize: 10, position: 'insideTopRight' }} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
          <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={40}>
            {breakdownData.map((entry) => (
              <Cell
                key={`bar-${entry.reg}`}
                fill={entry.lemon ? 'var(--danger)' : 'var(--primary)'}
                fillOpacity={entry.lemon ? 1 : 0.8}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}